import { useMemo } from 'react'
import range from 'lodash/range'
import { defineMessages, useIntl } from 'react-intl'

import { toLabel } from '@app/utils/timeConvert'

export const useAnnouncementsFilterOptions = (filter: { min_kids?: number; youngest_age_months?: number }) => {
  const { formatMessage } = useIntl()

  const minHoursOptions = useMemo(
    () =>
      new Array(24)
        .fill(0)
        .map((_v, i) => i + 1)
        .map(value => ({ label: formatMessage(messages.hours, { n: value }), value })),
    [formatMessage]
  )
  const maxHoursOptions = useMemo(
    () =>
      new Array(24)
        .fill(0)
        .map((_v, i) => i + 1)
        .map(value => ({ label: formatMessage(messages.hours, { n: value }), value })),
    [formatMessage]
  )

  const { startOptions, endOptions } = useMemo<{ startOptions: TimeOption[]; endOptions: TimeOption[] }>(() => {
    const start: TimeOption[] = []
    const end: TimeOption[] = []
    let iterDate = 0
    while (true) {
      const val = toLabel(iterDate)
      end.push({ label: val, value: val })
      start.push({ label: val, value: val })

      if (val === '23:30') break
      iterDate += 30
    }
    return { startOptions: start, endOptions: end }
  }, [])

  const sortOptions = useMemo<SortingOption[]>(
    () => [
      { label: formatMessage(messages.by_order_time), value: { by: 'start_time', direction: 'asc' } },
      { label: formatMessage(messages.new_first), value: { by: 'create_time', direction: 'desc' } },
      { label: formatMessage(messages.old_first), value: { by: 'create_time', direction: 'asc' } },
      { label: formatMessage(messages.long_first), value: { by: 'duration', direction: 'desc' } },
      { label: formatMessage(messages.short_first), value: { by: 'duration', direction: 'asc' } },
    ],
    [formatMessage]
  )

  const childrenMinCapacityOptions = useMemo<ChildrenCapacityOption[]>(
    () => [
      ...range(2, 6).map(i => ({
        label: formatMessage(messages.from_i, { i }),
        value: i,
      })),
    ],
    [formatMessage]
  )

  const childrenMaxCapacityOptions = useMemo<ChildrenCapacityOption[]>(() => {
    let options = [
      ...range(1, 6).map(i => ({
        label: formatMessage(messages.to_i, { i }),
        value: i,
      })),
    ]

    const min_kids = filter.min_kids
    if (min_kids) {
      options = options.filter(o => o.value === undefined || o.value >= min_kids)
    }
    return options
  }, [filter.min_kids, formatMessage])

  const childrenMinAgeOptions = useMemo<ChildrenAgeOption[]>(
    () => [
      { label: formatMessage(messages.from_n_months, { n: 1 }), value: 1 },
      { label: formatMessage(messages.from_half_a_year), value: 6 },
      { label: formatMessage(messages.from_n_years, { n: 1 }), value: 12 },
      { label: formatMessage(messages.from_n_years, { n: 2 }), value: 24 },
      { label: formatMessage(messages.from_n_years, { n: 3 }), value: 36 },
      { label: formatMessage(messages.from_n_years, { n: 4 }), value: 48 },
      { label: formatMessage(messages.from_n_years, { n: 5 }), value: 60 },
      { label: formatMessage(messages.from_n_years, { n: 6 }), value: 72 },
    ],
    [formatMessage]
  )

  const childrenMaxAgeOptions = useMemo<ChildrenAgeOption[]>(() => {
    let options = [
      { label: formatMessage(messages.to_n_months, { n: 1 }), value: 1 },
      { label: formatMessage(messages.to_half_a_year), value: 6 },
      { label: formatMessage(messages.to_n_years, { n: 1 }), value: 12 },
      { label: formatMessage(messages.to_n_years, { n: 2 }), value: 24 },
      { label: formatMessage(messages.to_n_years, { n: 3 }), value: 36 },
      { label: formatMessage(messages.to_n_years, { n: 4 }), value: 48 },
      { label: formatMessage(messages.to_n_years, { n: 5 }), value: 60 },
      { label: formatMessage(messages.to_n_years, { n: 14 }), value: 168 },
    ]

    const youngest_age_months = filter.youngest_age_months
    if (youngest_age_months) {
      options = options.filter(o => o.value === undefined || o.value >= youngest_age_months)
    }

    return options
  }, [filter.youngest_age_months, formatMessage])

  const regularOptions = useMemo<{ label: string; value: boolean }[]>(
    () => [
      { label: formatMessage(messages.only_regular), value: true },
      { label: formatMessage(messages.excluding_regular), value: false },
    ],
    [formatMessage]
  )

  const statusOptions = useMemo<{ label: string; value: 'all' | 'favorites' | 'except_ignored' }[]>(
    () => [
      { label: formatMessage(messages.everyone), value: 'all' },
      { label: formatMessage(messages.only_favorites), value: 'favorites' },
      { label: formatMessage(messages.except_ignored), value: 'except_ignored' },
    ],
    [formatMessage]
  )

  return {
    minHoursOptions,
    maxHoursOptions,
    startOptions,
    endOptions,
    sortOptions,
    childrenMinCapacityOptions,
    childrenMaxCapacityOptions,
    childrenMinAgeOptions,
    childrenMaxAgeOptions,
    regularOptions,
    statusOptions,
  }
}

export type TimeOption = {
  label: string
  value: string | undefined
}

export type ChildrenCapacityOption = {
  label: string
  value: number | undefined
}

export type ChildrenAgeOption = {
  label: string
  value: number | undefined
}

export type SortingOption = {
  label: string
  value: {
    by: 'start_time' | 'create_time' | 'duration' | 'distance'
    direction: 'asc' | 'desc'
  }
}

const messages = defineMessages({
  from_i: { id: "packages.announcementsFilter.useAnnouncementsFilterOptions.messages.from_i.18c2", defaultMessage: 'от {i}' },
  to_i: { id: "packages.announcementsFilter.useAnnouncementsFilterOptions.messages.to_i.e09e", defaultMessage: 'до {i}' },
  from_n_months: { id: "packages.announcementsFilter.useAnnouncementsFilterOptions.messages.from_n_months.ebd5", defaultMessage: 'от {n} {n, plural, one {месяца} other {месяцев}}' },
  from_half_a_year: { id: "packages.announcementsFilter.useAnnouncementsFilterOptions.messages.from_half_a_year.bfa9", defaultMessage: 'от полугода' },
  from_n_years: { id: "packages.announcementsFilter.useAnnouncementsFilterOptions.messages.from_n_years.a86d", defaultMessage: 'от {n} {n, plural, one {года} other {лет}}' },
  to_n_months: { id: "packages.announcementsFilter.useAnnouncementsFilterOptions.messages.to_n_months.1b65", defaultMessage: 'до {n} {n, plural, one {месяца} other {месяцев}}' },
  to_half_a_year: { id: "packages.announcementsFilter.useAnnouncementsFilterOptions.messages.to_half_a_year.e63e", defaultMessage: 'до полугода' },
  to_n_years: { id: "packages.announcementsFilter.useAnnouncementsFilterOptions.messages.to_n_years.8fce", defaultMessage: 'до {n} {n, plural, one {года} other {лет}}' },
  hours: { id: "packages.announcementsFilter.useAnnouncementsFilterOptions.messages.hours.d5d1", defaultMessage: '{n} {n, plural, one {час} few {часа} other {часов}}' },

  by_order_time: { id: "packages.announcementsFilter.useAnnouncementsFilterOptions.messages.by_order_time.066c", defaultMessage: 'По времени заказа' },
  new_first: { id: "packages.announcementsFilter.useAnnouncementsFilterOptions.messages.new_first.ea52", defaultMessage: 'Сначала новые' },
  old_first: { id: "packages.announcementsFilter.useAnnouncementsFilterOptions.messages.old_first.6dbb", defaultMessage: 'Сначала старые' },
  long_first: { id: "packages.announcementsFilter.useAnnouncementsFilterOptions.messages.long_first.ead0", defaultMessage: 'Сначала долгие' },
  short_first: { id: "packages.announcementsFilter.useAnnouncementsFilterOptions.messages.short_first.6961", defaultMessage: 'Сначала короткие' },

  only_regular: { id: "packages.announcementsFilter.useAnnouncementsFilterOptions.messages.only_regular.8fd5", defaultMessage: 'Только постоянные' },
  excluding_regular: { id: "packages.announcementsFilter.useAnnouncementsFilterOptions.messages.excluding_regular.6e71", defaultMessage: 'Кроме постоянных' },

  everyone: { id: "packages.announcementsFilter.useAnnouncementsFilterOptions.messages.everyone.984b", defaultMessage: 'Все' },
  only_favorites: { id: "packages.announcementsFilter.useAnnouncementsFilterOptions.messages.only_favorites.2aea", defaultMessage: 'Только избранные' },
  except_ignored: { id: "packages.announcementsFilter.useAnnouncementsFilterOptions.messages.except_ignored.9539", defaultMessage: 'Кроме исключенных' },
})
